import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { ApiError, enderecoDeArquivo } from '@/shared/api/client';
import {
  contextoDoTipo,
  templates,
  type DeliveryFormat,
  type Template,
  type TemplateVersion,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { useToast } from '@/shared/ui/useToast';

import { PaletaDeVariaveis } from './EditorDeCorpo';

/**
 * Modelo de contrato em Word.
 *
 * A edicao do arquivo acontece no editor de quem opera, e nao aqui. Escrever um
 * editor paginado proprio significaria reimplementar quebra de linha, controle de
 * viuva e orfa, fluxo de tabela entre paginas e metrica de fonte, e um editor que
 * erra a paginacao e pior do que nenhum: ele mostra tres paginas e imprime quatro.
 * Ver docs/ADR/0013-documentos-docx.md.
 *
 * O que a plataforma faz e o resto: guarda, versiona, encontra as marcacoes, troca
 * por valores preservando a formatacao e devolve o arquivo.
 */
export interface TemplateDocxPanelProps {
  template: Template;
  versaoAtual: TemplateVersion | undefined;
  onPublicado: () => void;
}

const TAMANHO_MAXIMO = 40 * 1024 * 1024;

export function TemplateDocxPanel({
  template,
  versaoAtual,
  onPublicado,
}: TemplateDocxPanelProps) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const podeEditar = can('templates.template.update');

  const entrada = useRef<HTMLInputElement>(null);
  const [notas, setNotas] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // A previa preenche o modelo com exemplos do contexto do tipo, sem gravar nada:
  // cada lista ganha dois itens, para que a repeticao de linha e de bloco apareca.
  const previa = useMutation({
    mutationFn: async ({
      version,
      format,
    }: {
      version: number;
      format: DeliveryFormat;
    }) => {
      const conteudo = await templates.previa(template.id, version, { format });
      return { conteudo, format };
    },
    onSuccess: ({ conteudo, format }) => {
      const endereco = URL.createObjectURL(conteudo);
      const ancora = document.createElement('a');
      ancora.href = endereco;
      ancora.download = `${template.name} previa.${format}`;
      document.body.append(ancora);
      ancora.click();
      ancora.remove();
      URL.revokeObjectURL(endereco);
    },
    onError: (causa: Error) => toast.error(mensagem(causa)),
  });

  const baixar = useMutation({
    mutationFn: (version: number) => templates.baixarVersao(template.id, version),
    onSuccess: (endereco) => {
      // O endereco leva direto ao armazenamento. Abrir em uma aba nova deixa a tela
      // do modelo intacta: quem baixa quase sempre continua editando depois.
      window.open(enderecoDeArquivo(endereco.url), '_blank', 'noopener,noreferrer');
    },
    onError: (causa: Error) => toast.error(mensagem(causa)),
  });

  async function enviar(arquivo: File) {
    setErro(null);

    if (!arquivo.name.toLowerCase().endsWith('.docx')) {
      setErro('Envie um documento do Word no formato .docx.');
      return;
    }
    if (arquivo.size > TAMANHO_MAXIMO) {
      setErro('O arquivo passa de 40 MB, que e o limite do modelo.');
      return;
    }

    setEnviando(true);
    try {
      const reserva = await templates.reservarEnvio(template.id);

      // O arquivo vai direto para o armazenamento, sem passar pela API: um contrato
      // de dezenas de megabytes atravessando a aplicacao nao acrescenta nada.
      const resposta = await fetch(enderecoDeArquivo(reserva.upload_url), {
        method: 'PUT',
        body: arquivo,
      });
      if (!resposta.ok) {
        throw new Error('O armazenamento recusou o arquivo.');
      }

      await templates.publicarArquivo(template.id, {
        staging_key: reserva.staging_key,
        filename: arquivo.name,
        notes: notas,
      });

      toast.success('Documento importado e publicado como a versao seguinte.');
      setNotas('');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      onPublicado();
    } catch (causa) {
      setErro(mensagem(causa));
    } finally {
      setEnviando(false);
      if (entrada.current) entrada.current.value = '';
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {erro ? (
        <Alert tone="danger" title="Nao foi possivel importar">
          {erro}
        </Alert>
      ) : null}

      {versaoAtual?.filename ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface-sunken px-3 py-2.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-ink-900">
              {versaoAtual.filename}
            </p>
            <p className="text-2xs text-ink-500">
              Versao {versaoAtual.version}, {formatarTamanho(versaoAtual.byte_size)}
            </p>
          </div>
          {/* Dois icones do Word lado a lado se confundiriam: o texto curto diz qual
              e o arquivo importado e qual e a previa. */}
          <div className="flex flex-wrap gap-1.5">
            <AcaoComIcone
              acao="baixarWord"
              rotulo="Baixar o Word"
              texto="Baixar"
              loading={baixar.isPending}
              onClick={() => baixar.mutate(versaoAtual.version)}
            />
            <AcaoComIcone
              acao="baixarWord"
              rotulo="Previa em Word"
              texto="Previa"
              loading={previa.isPending && previa.variables?.format === 'docx'}
              onClick={() =>
                previa.mutate({ version: versaoAtual.version, format: 'docx' })
              }
            />
            <AcaoComIcone
              acao="baixarPdf"
              rotulo="Previa em PDF"
              texto="Previa"
              loading={previa.isPending && previa.variables?.format === 'pdf'}
              onClick={() =>
                previa.mutate({ version: versaoAtual.version, format: 'pdf' })
              }
            />
          </div>
        </div>
      ) : (
        <Alert tone="info" title="Nenhum documento importado ainda">
          Este modelo guarda um arquivo do Word. Traga o contrato ja formatado, com as
          margens, o cabecalho e o rodape que ele precisa ter, e insira as variaveis no
          proprio Word.
        </Alert>
      )}

      {versaoAtual?.body ? (
        <div>
          <p className="text-xs font-semibold text-ink-700">Texto do documento</p>
          <p className="mt-1 text-2xs text-ink-500">
            Extraido do arquivo, para leitura e busca. A formatacao vive no .docx.
          </p>
          <pre className="mt-2 max-h-56 overflow-auto rounded-md border border-line bg-surface-sunken p-3 font-mono text-2xs whitespace-pre-wrap text-ink-700">
            {versaoAtual.body}
          </pre>
        </div>
      ) : null}

      <div>
        <p className="text-xs font-semibold text-ink-700">Variaveis disponiveis</p>
        <p className="mt-1 text-2xs text-ink-500">
          Escreva a marcacao no Word, no ponto onde o valor deve aparecer. Clique para
          copiar. Ela pode ficar em qualquer lugar do documento, inclusive no cabecalho,
          no rodape e dentro de tabela. A linha de tabela com marcacao de uma lista se
          repete uma vez por item; para repetir paragrafos, ponha {'{{repetir.lista}}'}{' '}
          e {'{{fim.lista}}'} sozinhos, cada um no seu paragrafo, antes e depois do
          trecho. Texto rico sozinho no paragrafo entra com paragrafos e negrito, com o
          estilo do paragrafo do modelo.
        </p>
        <div className="mt-2">
          <PaletaDeVariaveis
            contexto={contextoDoTipo[template.kind]}
            onEscolher={(marcacao) => {
              navigator.clipboard
                .writeText(marcacao)
                .then(() => toast.success(`${marcacao} copiada.`))
                .catch(() =>
                  toast.error('Nao foi possivel copiar. Anote a marcacao a mao.'),
                );
            }}
          />
        </div>
      </div>

      {podeEditar ? (
        <div className="flex flex-col gap-3 border-t border-line pt-4">
          <Field
            label="Nota da versao"
            value={notas}
            hint="O que mudou neste documento e por que. Sem isso, so a comparacao dos arquivos explica."
            onChange={(evento) => setNotas(evento.target.value)}
          />

          <input
            ref={entrada}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(evento) => {
              const arquivo = evento.target.files?.[0];
              if (arquivo) void enviar(arquivo);
            }}
          />

          <div className="flex flex-wrap gap-2">
            <Button loading={enviando} onClick={() => entrada.current?.click()}>
              {versaoAtual?.filename ? 'Importar nova versao' : 'Importar documento'}
            </Button>
          </div>

          <p className="text-2xs text-ink-500">
            Cada importacao publica uma versao nova. A anterior continua guardada e
            continua explicando os contratos que ja gerou.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function formatarTamanho(bytes: number): string {
  if (bytes <= 0) return 'tamanho desconhecido';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function mensagem(causa: unknown): string {
  if (causa instanceof ApiError) return causa.message;
  if (causa instanceof Error) return causa.message;
  return 'Nao foi possivel concluir. Tente novamente.';
}
